/**
 * Puts the consent screen in front of everything until feedback is granted
 * (#373, D30).
 *
 * Rendered inside the app shell, around the routed page: the header and the
 * skip link stay, the page does not. A signed-in learner whose `feedback`
 * decision is not `granted` under the current statement (never decided,
 * withdrawn, or granted to an older text) sees `ConsentScreen` instead of
 * whatever they asked for, and nothing behind it mounts - so no screen fires a
 * request that the server's own guard would refuse with `consent_required`.
 *
 * `/settings/consent` (it IS the screen) and `/settings/data` (export and delete
 * must work without consent) are never replaced.
 *
 * **While the question is being asked, nothing is shown.** Rendering the page
 * first and replacing it would flash a screen the learner has not been cleared
 * to use and fire its requests.
 *
 * **If the question cannot be answered, the page renders.** A failure here (the
 * server is down, the session ended) is the page's own failure to report, in its
 * own words; showing a consent screen for a transport fault would claim
 * something about the learner's decision that is not known. The server enforces
 * regardless - a write without consent is refused whatever this component
 * decides - so failing open in the UI costs a clearer error, never a stored row.
 */

import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { ConsentScreen } from './consent-screen.js';

/** The route that is always the consent screen itself. */
export const CONSENT_SETTINGS_PATH = '/settings/consent';

/**
 * Routes the gate never replaces. The consent screen is itself; the data screen
 * is where a learner exports or deletes what DaVinci holds, and that must work
 * without consent - withdrawing is never a reason to be unable to take your data
 * or remove it, and the contact page is how to ask about that (the server exempts export and deletion for the same reason,
 * `http/consent-routes.ts`). It also stores no work and fetches nothing on load.
 */
const NEVER_GATED: ReadonlySet<string> = new Set([CONSENT_SETTINGS_PATH, '/settings/data', '/settings/contact']);

export function ConsentGate({ children }: { readonly children: React.ReactNode }): React.JSX.Element | null {
  const api = useApi();
  const location = useLocation();
  const exempt = NEVER_GATED.has(location.pathname);
  // Not even asked on an exempt route: /settings/data fetches nothing on load, on purpose.
  const decisions = useQuery({
    queryKey: queryKeys.consentDecisions,
    queryFn: () => api.consentDecisions(),
    enabled: !exempt,
  });

  if (exempt) return <>{children}</>;
  if (decisions.isPending) return null;
  if (decisions.isError) return <>{children}</>;

  const feedback = decisions.data.decisions.find((d) => d.purpose === 'feedback');
  if (feedback?.state === 'granted') return <>{children}</>;
  return <ConsentScreen required />;
}
